import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../services/api";

interface GiftCard {
  id: string;
  brand: string;
  amount: number;
}

interface Redemption {
  id: string;
  userId: string;
  giftCardId: string;
  giftCard: GiftCard;
  pointAmount: number;
  status: string;
  fulfillmentCode: string | null;
  fulfilledAt: string | null;
  createdAt: string;
  userEmail?: string;
}

export default function AdminRedemptionsPage() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedRedemption, setSelectedRedemption] = useState<Redemption | null>(null);
  const [fulfillmentCode, setFulfillmentCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "redemptions", statusFilter],
    queryFn: async () => {
      const response = await api.get<{ redemptions: Redemption[] }>(
        `/admin/redemptions?status=${statusFilter}`
      );
      return response.data.redemptions;
    },
  });

  const fulfillMutation = useMutation({
    mutationFn: async (redemptionId: string) => {
      const response = await api.post(`/admin/redemptions/${redemptionId}/fulfill`, {
        fulfillmentCode,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "redemptions"] });
      setSelectedRedemption(null);
      setFulfillmentCode("");
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to fulfill redemption");
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (redemptionId: string) => {
      const response = await api.post(`/admin/redemptions/${redemptionId}/cancel`, {
        reason: "Cancelled by admin",
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "redemptions"] });
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to cancel redemption");
    },
  });

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      pending: "bg-yellow-500/20 text-yellow-400",
      fulfilled: "bg-green-500/20 text-green-400",
      cancelled: "bg-red-500/20 text-red-400",
    };
    return badges[status] || "bg-gray-500/20 text-gray-400";
  };

  const handleCancel = (redemptionId: string) => {
    if (confirm("Are you sure you want to cancel this redemption? This action cannot be undone.")) {
      cancelMutation.mutate(redemptionId);
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-300"
          >
            &times;
          </button>
        </div>
      )}

      <div>
        <h1 className="text-3xl font-bold text-white">Redemptions</h1>
        <p className="text-gray-400 mt-2">
          Manage gift card redemption requests
        </p>
      </div>

      {/* Status Filter */}
      <div className="card">
        <div className="flex gap-4">
          {["pending", "fulfilled", "cancelled"].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg capitalize ${
                statusFilter === status
                  ? "bg-primary-500 text-white"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Redemptions List */}
      <div className="card">
        {isLoading ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : !data?.length ? (
          <div className="text-center py-8 text-gray-400">
            No {statusFilter} redemptions
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {data.map((redemption) => (
              <div key={redemption.id} className="py-4 flex justify-between items-start">
                <div>
                  <div className="font-medium text-white">
                    ${redemption.giftCard.amount} {redemption.giftCard.brand} Gift Card
                  </div>
                  <div className="text-sm text-gray-400">
                    {redemption.pointAmount} points - {redemption.userEmail || redemption.userId}
                  </div>
                  <div className="text-sm text-gray-500">
                    Requested: {new Date(redemption.createdAt).toLocaleString()}
                  </div>
                  {redemption.fulfillmentCode && (
                    <div className="mt-2 p-2 bg-gray-800 rounded font-mono text-sm">
                      Code: {redemption.fulfillmentCode}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm capitalize ${getStatusBadge(redemption.status)}`}>
                    {redemption.status}
                  </span>
                  {redemption.status === "pending" && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedRedemption(redemption)}
                        className="btn btn-primary text-sm"
                      >
                        Fulfill
                      </button>
                      <button
                        onClick={() => handleCancel(redemption.id)}
                        className="btn bg-red-600 hover:bg-red-700 text-sm"
                        disabled={cancelMutation.isPending}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fulfillment Modal */}
      {selectedRedemption && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-white mb-4">Fulfill Redemption</h3>
            <div className="space-y-4 mb-6">
              <div className="bg-gray-700 p-4 rounded-lg">
                <div className="text-white font-medium">
                  ${selectedRedemption.giftCard.amount} {selectedRedemption.giftCard.brand}
                </div>
                <div className="text-gray-400 text-sm">
                  User: {selectedRedemption.userEmail || selectedRedemption.userId}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Gift Card Code
                </label>
                <input
                  type="text"
                  value={fulfillmentCode}
                  onChange={(e) => setFulfillmentCode(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  placeholder="Enter the gift card code"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedRedemption(null);
                  setFulfillmentCode("");
                }}
                className="flex-1 btn bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => fulfillMutation.mutate(selectedRedemption.id)}
                className="flex-1 btn btn-primary"
                disabled={!fulfillmentCode || fulfillMutation.isPending}
              >
                {fulfillMutation.isPending ? "Fulfilling..." : "Fulfill"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
